<template>
  <!-- Add MFA POPUP -->
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="cancel">
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="min-h-screen px-4 text-center">
          <TransitionChild
            as="changepassword"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0" />
          </TransitionChild>
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <TransitionChild
            as="profile"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="
                inline-block
                w-full
                max-w-md
                overflow-hidden
                text-center
                align-middle
                transition-all
                transform
                bg-white
                shadow-xl
                rounded-2xl
                justify-center
                border border-gray-300
                pb-6
              "
            >
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900 text-center mt-6"
              >
                Manage Your 2-Factor Login
              </DialogTitle>
              <div id="recapcha-element"></div>
              <!-- text part begins below -->
              <div class="p-4 text-sm text-gray-700">
                <div v-if="step=='primary_login'">
                  Managing 2-Factor login requires you to login first
                </div>
                <div v-if="step=='login_send_code'" class="space-y-4">
                  <p class="text-base font-bold">
                    Verify with your phone
                  </p>
                  <p>
                    Send a code via SMS to {{ phone_number }}
                  </p>
                  <p>
                    Carrier messaging charges may apply
                  </p>
                </div>
                <div v-if="step=='login_verify_code'" class="space-y-4">
                  <p class="text-base font-bold">
                    Verify with your phone
                  </p>
                  <p>
                    A code was sent to {{ phone_number }}. Enter the code below to verify:
                  </p>
                </div>
                <div v-if="step=='enroll_begin'">
                  Please input your phone number to enroll in 2-factor SMS verification code; 
                  currently, we only support US phone numbers
                </div>
                <div v-if="step=='enroll_verify'" class="space-y-4">
                  <p class="text-base font-bold">
                    Verify with your phone
                  </p>
                  <p>
                    A code was sent to {{ phone_number }}. Enter the code below to verify:
                  </p>
                </div>
              </div>
              <!-- input part begins below -->
              <div class="p-4">
                <div v-if="error" class="sm:mx-auto sm:w-full sm:max-w-md">
                  <ErrorAlert v-on:close="closeError" v-bind:error="error"></ErrorAlert>
                </div>
                <div v-if="info" class="sm:mx-auto sm:w-full sm:max-w-md">
                  <InfoAlert v-on:close="closeInfo" v-bind:info="info"></InfoAlert>
                </div>
                <div class="my-3" v-if="step=='primary_login'">
                  <div class="w-1/3 inline-block">Password:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="password"
                      :size="18"
                      v-model="password"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      -ml-3
                      rounded-md
                      "
                    />
                  </div>
                </div>
                <div class="my-3" v-if="step=='login_verify_code'">
                  <div class="w-1/3 inline-block">Code:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="text"
                      :size="18"
                      v-model="login_verification_code"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      -ml-3
                      rounded-md
                      "
                    />
                  </div>
                </div>
                <div class="my-3" v-if="step=='enroll_begin'">
                  <div class="w-1/3 inline-block">Phone Number:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="tel"
                      :size="18"
                      id="phone"
                      v-model="phone_number_input"
                      @input="formatPhoneNumber"
                      @keydown="handleKeyDown"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      ml-3
                      rounded-md
                      "
                    />
                  </div>
                </div>
                <div class="my-3" v-if="step=='enroll_verify'">
                  <div class="w-1/3 inline-block">Code:</div>
                  <div class="w-1/2 inline-block">
                    <input
                      type="text"
                      :size="18"
                      v-model="enroll_verification_code"
                      class="
                      block
                      w-full
                      shadow-sm
                      text-md
                      focus:ring-indigo-500
                      focus:border-indigo-500
                      border-gray-300
                      -ml-3
                      rounded-md
                      "
                    />
                  </div>
                </div>
                <div class="my-3" v-if="step=='enroll_finish'">
                  <div class="inline-block">
                    You have enrolled 2-Factor login with phone number: {{ phone_number }}
                  </div>
                </div>
              </div>

              <!-- button part begins below -->
              <div class="flex flex-row my-4 justify-center mb-4">
                <span class="space-x-2">
                  <button v-if="step=='primary_login'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="log_in"
                  >
                    Login
                  </button>
                  <button v-if="step=='login_send_code'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="login_sendCode"
                  >
                    Receive a code via SMS
                  </button>
                  <button v-if="step=='login_verify_code'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="login_verifyCode"
                  >
                    Verify Code
                  </button>
                  <button v-if="step=='enroll_begin'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="sendEnrollVerificationCode"
                  >
                    Send Verification Code
                  </button>
                  <button v-if="step=='enroll_verify'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="verifyEnrollCode"
                  >
                    Verify Code
                  </button>
                  <button v-if="step=='enroll_finish'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="ok"
                  >
                    OK
                  </button>
                  <button v-if="step=='enroll_finish'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="confirm_unenroll"
                  >
                    Unenroll this phone number
                  </button>
                  <UnenrollConfirmation
                    :open="confirm_unenroll_open"
                    :phone_number="phone_number"
                    v-on:unenroll="unenroll"
                    v-on:close="cancel_unenroll"
                    />
                  <button v-if="step=='enroll_begin' || step=='enroll_verify'"
                    type="button"
                    class="
                      inline-flex
                      justify-center
                      px-4
                      py-2
                      text-sm
                      font-medium
                      text-white
                      bg-custom-purple-500
                      border border-transparent
                      rounded-md
                      hover:bg-custom-purple-900
                      focus:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-offset-2
                      focus-visible:ring-custom-purple-500
                    "
                    @click="cancel"
                  >
                    Cancel
                  </button>
                </span>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  DialogTitle,
} from "@headlessui/vue";

import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import InfoAlert from "@/components/alerts/InfoAlert.vue";
import firebase from "firebase/app";
import "firebase/auth";
import { login } from "@/services/user.js";
import { setAuthToken } from "@/services/backend-api";
import UnenrollConfirmation from "@/components/general/UnenrollConfirmation.vue";
import { AsYouType } from "libphonenumber-js";

export default {
  props: ["isOpen"],
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    ErrorAlert,
    InfoAlert,
    TransitionChild,
    TransitionRoot,
    UnenrollConfirmation
  },
  data() {
    return {
      email: "",
      password: "",
      login_verification_id: "",
      login_verification_code: "",

      phone_number: "",
      phone_number_input: "",
      enroll_verification_code: "",
      enroll_verification_id: "",

      info: null,
      error: null,
      user: null,

      step: "primary_login",
      already_enrolled: false,
      recaptchaVerifier: null,
      resolver: null,

      confirm_unenroll_open: false,

      isDeleting: false,
    };
  },
  mounted() {
    const auth = firebase.auth();
    this.email = auth.currentUser.email;
  },
  methods: {
    closeModal() {
      this.$emit("close");
      this.info = null;
      this.error = null;
    },
    closeInfo() {
      this.info = null;
    },
    closeError() {
      this.error = null;
    },
    ok() {
      if (this.already_enrolled)
        this.step = 'enroll_finish';
      else if (this.user)
        this.step = 'enroll_begin';
      else
        this.step = 'primary_login';
      this.closeModal();
    },
    cancel() {
      if (this.already_enrolled) {
        this.enroll_verification_code = "";
        this.step = 'enroll_finish';
      }
      else if (this.user) {
        this.phone_number = "";
        this.phone_number_input = "";
        this.enroll_verification_code = "";
        this.step = "enroll_begin";
      }
      else {
        this.phone_number = "";
        this.phone_number_input = "";
        this.enroll_verification_code = "";
        this.step = "primary_login";
      }
      this.closeModal();
    },

    log_in() {
      this.error = null;
      login({email: this.email, password: this.password}).then ( (userCredential) => {
        if (userCredential) {
          this.user = userCredential.user;
          var idToken = this.user.getIdToken();
          setAuthToken(idToken);
          this.step = "enroll_begin";
        }
      })
      .catch( (e) => {
        if (e.code == 'auth/multi-factor-auth-required') {
          // 2-factor login required
          this.resolver = e.resolver;
          if (this.resolver && this.resolver.hints.length > 0) {
            this.phone_number = this.resolver.hints[0].phoneNumber;
            this.step = "login_send_code";
          }
        }
        else if (e.message && (e.message.includes("INVALID_LOGIN_CREDENTIALS") || e.message.includes("There is no user record corresponding"))) {
          e.message = "Invalid Email or Password. Please try again.";
          this.error = e;
        } else {
          e.message = "Unknown Error encountered.  Please Try again or contact support.";
          this.error = e;
        }
      })
    },
    login_sendCode() {
      this.error = null;
      if (this.resolver && this.resolver.hints.length > 0) {
        var phoneInfoOptions = {
          multiFactorHint: this.resolver.hints[0],
          session: this.resolver.session
        };
        var phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
        if (!this.recaptchaVerifier) {
          this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recapcha-element', {
            size: 'invisible', // or 'normal' for a visible CAPTCHA
          });
        }
        else {
          this.recaptchaVerifier.clear();
          this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recapcha-element', {
            size: 'invisible', // or 'normal' for a visible CAPTCHA
          });
        }
        // Send SMS verification code
        phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier)
        .then( (verificationId) => {
          this.login_verification_id = verificationId;
          this.step = "login_verify_code"
        })
        .catch ((e) => {
          this.error = e;
        });
      }
    },
    login_verifyCode() {
      this.error = null;
      var cred = firebase.auth.PhoneAuthProvider.credential(this.login_verification_id, this.login_verification_code);
      var multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);
      if (this.resolver)
        this.resolver.resolveSignIn(multiFactorAssertion)
        .then( (userCredential) => {
          if (userCredential) {
            this.user = userCredential.user;
            var idToken = this.user.getIdToken();
            setAuthToken(idToken);
            this.step = "enroll_finish";
            this.already_enrolled = true;
            this.login_verification_code = "";
            this.login_verification_id = "";
          }
        })
        .catch( (e)=> {
          this.error = e;
        })
    },
    
    sendEnrollVerificationCode() {
      this.error = null;
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      if (!phoneRegex.test(this.phone_number)) {
        this.error = {message: "Invalid phone number"};
        return;
      }
      
      const inputPhoneNumber = this.phone_number;
      if (!this.recaptchaVerifier) {
        this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recapcha-element', {
          size: 'invisible', // or 'normal' for a visible CAPTCHA
        });
      }
      else {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recapcha-element', {
          size: 'invisible', // or 'normal' for a visible CAPTCHA
        });
      }

      this.user.multiFactor.getSession().then((multiFactorSession) => {
        // Specify the phone number and pass the MFA session.
        var phoneInfoOptions = {
          phoneNumber: inputPhoneNumber,
          session: multiFactorSession
        };
        var phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
        // Send SMS verification code.
        return phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions, this.recaptchaVerifier);
      })
      .then((verificationId) => {
        this.enroll_verification_id = verificationId;
        this.step = "enroll_verify";
      })
      .catch ((e) => {
        this.error = e;
      });
    },
    verifyEnrollCode() {
      this.error = null;
      var cred = firebase.auth.PhoneAuthProvider.credential(this.enroll_verification_id, this.enroll_verification_code);
      var multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);
      this.user.multiFactor.enroll(multiFactorAssertion, this.phone_number).then(() => {
        this.already_enrolled = true;
        this.step = "enroll_finish";
      })
      .catch ((e) => {
        this.error = e;
      });
    },
    confirm_unenroll() {
      this.confirm_unenroll_open = true;
    },
    cancel_unenroll() {
      this.confirm_unenroll_open = false;
      this.closeModal();
    },
    unenroll() {
      const mfaInfo = this.user.multiFactor;
      const enrolledFactors = mfaInfo.enrolledFactors;

      if (enrolledFactors.length > 0) {
        const phoneFactor = enrolledFactors[0]

        if (phoneFactor) {
          mfaInfo.unenroll(phoneFactor.uid).then(() => {
            this.already_enrolled = false;
            this.phone_number = "";
            this.phone_number_input = "";
            this.enroll_verification_code = "";
            this.enroll_verification_id = "";
            this.step = 'enroll_begin';
          });
        }
      }
      this.confirm_unenroll_open = false;
      this.closeModal();
    },
    formatPhoneNumber() {
      const asYouType = new AsYouType('US');
      if (this.isDeleting) {
        asYouType.input(this.phone_number_input);
      } else {
        const formatted = asYouType.input(this.phone_number_input);
        this.phone_number_input = formatted;
      }
      this.phone_number = asYouType.getNumber() ? asYouType.getNumber().number : "";
      console.log(this.phone_number);
    },
    handleKeyDown(event) {
      // Check if the user is pressing Backspace or Delete
      this.isDeleting = event.key === 'Backspace' || event.key === 'Delete';
    },
  },
};
</script>

